import { motion } from 'framer-motion'
import { useTranslation, Trans } from 'react-i18next'
import { pageUrl } from '../seo'
import { Phone, Mail, Clock, MapPin, Shield, Scale, Heart, Users, Building2 } from 'lucide-react'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

// Só os ícones vivem aqui; os rótulos vêm de apoio.servicos, pela ordem.
const SERVICE_ICONS = [Scale, Heart, Building2, Shield, Users]

export default function Apoio() {
    const { t } = useTranslation()
    const servicos = t('apoio.servicos', { returnObjects: true })
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            <title>{t('apoio.metaTitle')}</title>
            <meta name="description" content={t('apoio.metaDescription')} />
            {/* pageUrl() já inclui a base da língua (/, /en/, /uk/, …) */}
            <link rel="canonical" href={pageUrl('apoio')} />
            <meta property="og:url" content={pageUrl('apoio')} />
            {/* Hero */}
            <section className="py-16 lg:py-24" style={{ backgroundColor: '#00203F' }}>
                <div className="container-custom">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="font-heading text-4xl lg:text-5xl font-bold text-white leading-tight"
                    >
                        {t('apoio.h1')}
                    </motion.h1>
                    <div className="mt-6 h-1 w-24 rounded-full" style={{ backgroundColor: '#ADEFD1' }} />
                </div>
            </section>

            {/* Intro strip */}
            <section style={{ backgroundColor: '#ADEFD1' }}>
                <div className="container-custom py-8">
                    <p className="text-base font-semibold leading-relaxed max-w-3xl" style={{ color: '#00203F' }}>
                        <Trans i18nKey="apoio.intro" components={{ strong: <strong /> }} />
                    </p>
                </div>
            </section>

            {/* Services + SAFE card */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-5 gap-12 items-start max-w-6xl mx-auto">

                        {/* Left: services (3/5) */}
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="lg:col-span-3 space-y-8"
                        >
                            <div>
                                <h2 className="font-heading text-2xl font-bold mb-6 text-navy">
                                    {t('apoio.prestamosTitulo')}
                                </h2>
                                <p className="text-base leading-relaxed mb-6" style={{ color: '#00203F' }}>{t('apoio.prestamosIntro')}</p>
                                <div className="space-y-3">
                                    {SERVICE_ICONS.map((Icon, i) => (
                                        <div
                                            key={i}
                                            className="flex items-start gap-4 p-4 rounded-xl"
                                            style={{ backgroundColor: 'rgba(0,32,63,0.04)', border: '1px solid rgba(0,32,63,0.08)' }}
                                        >
                                            <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#00203F' }}>
                                                <Icon size={16} style={{ color: '#ADEFD1' }} />
                                            </div>
                                            <p className="text-base leading-relaxed pt-1" style={{ color: '#00203F' }}>{servicos[i]}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-4 text-base leading-relaxed" style={{ color: '#00203F' }}>
                                <h2 className="font-heading text-xl font-bold text-navy">
                                    {t('apoio.safe.titulo')}
                                </h2>
                                <p>{t('apoio.safe.p1')}</p>
                                <p>{t('apoio.safe.p2')}</p>
                                <p>{t('apoio.safe.p3')}</p>
                                <p>{t('apoio.safe.p4')}</p>
                                <p>{t('apoio.safe.p5')}</p>
                            </div>
                        </motion.div>

                        {/* Right: SAFE contact card (2/5) */}
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="lg:col-span-2 lg:sticky lg:top-28"
                        >
                            <div className="rounded-2xl overflow-hidden shadow-xl" style={{ border: '1px solid rgba(0,32,63,0.12)' }}>
                                {/* Card header */}
                                <div className="p-6" style={{ backgroundColor: '#00203F' }}>
                                    <img
                                        src="/assets/images/logo_apavSAFE.png"
                                        alt={t('apoio.safe.logoAlt')}
                                        className="h-24 object-contain mb-4"
                                    />
                                </div>

                                {/* Contact details */}
                                <div className="p-6 space-y-4 bg-white">
                                    <div className="flex gap-3 items-start">
                                        <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0,32,63,0.08)' }}>
                                            <MapPin size={15} style={{ color: '#00203F' }} />
                                        </div>
                                        <div className="text-base leading-relaxed" style={{ color: '#00203F' }}>
                                            <p className="font-bold mb-0.5">{t('apoio.contacto.morada')}</p>
                                            <p translate="no">Rua José Estêvão 135 A<br />1150-201 LISBOA</p>
                                        </div>
                                    </div>

                                    <div className="flex gap-3 items-start">
                                        <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0,32,63,0.08)' }}>
                                            <Phone size={15} style={{ color: '#00203F' }} />
                                        </div>
                                        <div className="text-base" style={{ color: '#00203F' }}>
                                            <p className="font-bold mb-0.5">{t('apoio.contacto.telefone')}</p>
                                            <a href="tel:213587914" className="font-bold hover:underline text-base" style={{ color: '#00203F' }}>{t('apoio.contacto.telefoneValor')}</a>
                                        </div>
                                    </div>

                                    <div className="flex gap-3 items-start">
                                        <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0,32,63,0.08)' }}>
                                            <Mail size={15} style={{ color: '#00203F' }} />
                                        </div>
                                        <div className="text-base" style={{ color: '#00203F' }}>
                                            <p className="font-bold mb-0.5">{t('apoio.contacto.email')}</p>
                                            <a href="mailto:safe@apav.pt" className="hover:underline" style={{ color: '#00203F' }}>{t('apoio.contacto.emailValor')}</a>
                                        </div>
                                    </div>

                                    <div className="flex gap-3 items-start">
                                        <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0,32,63,0.08)' }}>
                                            <Clock size={15} style={{ color: '#00203F' }} />
                                        </div>
                                        <div className="text-base" style={{ color: '#00203F' }}>
                                            <p className="font-bold mb-0.5">{t('apoio.contacto.horario')}</p>
                                            <p>{t('apoio.contacto.horarioValor')}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* CTA */}
                                <div className="px-6 pb-6 bg-white">
                                    <a
                                        href="mailto:safe@apav.pt"
                                        className="block w-full text-center font-bold text-sm py-3 px-6 rounded-xl transition-opacity hover:opacity-90"
                                        style={{ backgroundColor: '#00203F', color: '#ADEFD1', fontFamily: 'Rubik, system-ui, sans-serif' }}
                                    >
                                        {t('apoio.contacto.cta')}
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </motion.div>
    )
}
